<template>
  <form class="customer-contact-form" @submit.prevent="handleSubmit">
    <div class="customer-contact-form__row">
      <TextInput
        id="customer_contact_first_name"
        :label="$t('customer_accounts.contacts.fields.first_name')"
        v-model="form.first_name"
        :error="errors.name"
      />
      <TextInput
        id="customer_contact_last_name"
        :label="$t('customer_accounts.contacts.fields.last_name')"
        v-model="form.last_name"
      />
    </div>

    <div class="customer-contact-form__row">
      <TextInput
        id="customer_contact_email"
        type="email"
        :label="$t('customer_accounts.contacts.fields.email')"
        v-model="form.email"
        :error="errors.email"
      />
      <TextInput
        id="customer_contact_phone"
        :label="$t('customer_accounts.contacts.fields.phone')"
        v-model="form.phone"
      />
    </div>

    <div class="customer-contact-form__row">
      <TextInput
        id="customer_contact_job_title"
        :label="$t('customer_accounts.contacts.fields.job_title')"
        v-model="form.job_title"
        :placeholder="$t('customer_accounts.contacts.fields.job_title_placeholder')"
      />
      <SelectInput
        id="customer_contact_site"
        :label="$t('customer_accounts.contacts.fields.site')"
        v-model="form.site_id"
        :options="siteSelectOptions"
      />
    </div>

    <div class="customer-contact-form__roles">
      <span class="customer-contact-form__label">
        {{ $t('customer_accounts.contacts.fields.roles') }}
      </span>
      <label v-for="role in roles" :key="role" class="customer-contact-form__role">
        <input type="checkbox" :value="role" v-model="form.roles" />
        <span>
          <strong>{{ $t(`customer_accounts.contacts.roles.${role}`) }}</strong>
          <small>{{ $t(`customer_accounts.contacts.role_hints.${role}`) }}</small>
        </span>
      </label>
    </div>

    <label class="customer-contact-form__role">
      <input type="checkbox" v-model="form.is_primary" />
      <span>
        <strong>{{ $t('customer_accounts.contacts.fields.is_primary') }}</strong>
        <small>
          {{
            form.site_id
              ? $t('customer_accounts.contacts.primary_hint_site')
              : $t('customer_accounts.contacts.primary_hint_customer')
          }}
        </small>
      </span>
    </label>

    <TextAreaInput
      id="customer_contact_notes"
      :label="$t('customer_accounts.contacts.fields.notes')"
      v-model="form.notes"
      :rows="2"
    />

    <div class="customer-contact-form__actions">
      <Btn type="button" variant="outlined" @click="onCancel && onCancel()">
        {{ $t('common.cancel') }}
      </Btn>
      <Btn type="submit" variant="primary" :loading="saving">
        {{ isEdit ? $t('common.save') : $t('customer_accounts.contacts.add') }}
      </Btn>
    </div>
  </form>
</template>

<script>
  import { defineComponent, ref, computed, getCurrentInstance } from 'vue';
  import Btn from '@/components/partials/Btn.vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import TextAreaInput from '@/components/inputs/TextAreaInput.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import { CONTACT_ROLES, siteOptions } from '@shared/utils/customerAccounts';

  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  export default defineComponent({
    name: 'CustomerContactModal',
    components: { Btn, TextInput, TextAreaInput, SelectInput },
    props: {
      contact: { type: Object, default: null },
      sites: { type: Array, default: () => [] },
      defaultSiteId: { type: String, default: null },
      onSubmit: { type: Function, required: true },
      onCancel: { type: Function, default: null },
    },
    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const isEdit = computed(() => !!props.contact?.id);
      const saving = ref(false);
      const errors = ref({});

      const c = props.contact || {};
      const form = ref({
        first_name: c.first_name || '',
        last_name: c.last_name || '',
        email: c.email || '',
        phone: c.phone || '',
        job_title: c.job_title || '',
        site_id: c.site_id || props.defaultSiteId || '',
        roles: [...(c.roles || [])],
        is_primary: !!c.is_primary,
        notes: c.notes || '',
      });

      const siteSelectOptions = computed(() =>
        siteOptions(props.sites, { emptyLabel: $t('customer_accounts.contacts.whole_customer') })
      );

      const validate = () => {
        errors.value = {};
        const f = form.value;
        if (!f.first_name.trim() && !f.last_name.trim() && !f.email.trim()) {
          errors.value.name = $t('customer_accounts.contacts.validation.name_or_email');
        }
        if (f.email.trim() && !EMAIL_PATTERN.test(f.email.trim())) {
          errors.value.email = $t('customer_accounts.contacts.validation.invalid_email');
        }
        return Object.keys(errors.value).length === 0;
      };

      const handleSubmit = async () => {
        if (!validate()) return;
        saving.value = true;
        try {
          const f = form.value;
          await props.onSubmit({
            first_name: f.first_name.trim() || null,
            last_name: f.last_name.trim() || null,
            email: f.email.trim() || null,
            phone: f.phone.trim() || null,
            job_title: f.job_title.trim() || null,
            site_id: f.site_id || null,
            roles: f.roles,
            is_primary: f.is_primary,
            notes: f.notes.trim() || null,
          });
        } finally {
          saving.value = false;
        }
      };

      return {
        form,
        errors,
        saving,
        isEdit,
        roles: CONTACT_ROLES,
        siteSelectOptions,
        handleSubmit,
      };
    },
  });
</script>

<style scoped lang="scss">
  .customer-contact-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &__row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.75rem;

      @include sm-down {
        grid-template-columns: 1fr;
      }
    }

    &__label {
      font-size: 0.875rem;
      font-weight: 500;
      color: var(--slate-700);
    }

    &__roles {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    &__role {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      cursor: pointer;

      input {
        margin-top: 0.2rem;
      }

      span {
        display: flex;
        flex-direction: column;
      }

      strong {
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--slate-800);
      }

      small {
        font-size: 0.75rem;
        color: var(--slate-500);
      }
    }

    &__actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
      padding-top: 0.75rem;
      border-top: 1px solid var(--slate-200);
    }
  }
</style>
